import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { FontAwesomeIcon } from "./FontAwesome";
import { QuestionValidationErrorIcon } from "./ValidationErrorIcons";
import { ValidationErrorModel } from "../../models/ValidationErrorModel";
import { translate } from "../../models/utilities/translationUtilities";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import useStyles from "./ValidationError.styles";
import useDimensionContext from "../../hooks/useDimensionContext";
import { QuestionModel } from "../../models/QuestionModel";
import useRecall from "../../hooks/useRecall";
import { QuestionType } from "../../models/enums/QuestionType";
import { useSelector } from "react-redux";
import { StoreState } from "../../models/states/StoreState";
import type { Theme } from "../../types/Theme";

interface Props {
    error: ValidationErrorModel;
    question: QuestionModel;
    questionnaire: QuestionnaireModel;
    language: string;
}

function replaceArgs(text: string, args: { [name: string]: string }) {
    if (text == null || args == null) return text;
    return text.replace(/{(.*?)}/g, (x, g) => args[g]);
}

function getCustomErrorMessage(props: Props) {
    const { error, question, language } = props;
    const customErrorKey = "CustomErrorMessage";
    if (error.key === customErrorKey && error.message) {
        return error.message;
    }
    return (
        translate(question, customErrorKey, language) ||
        getSingleColumnCustomErrorMessage(question, customErrorKey, language)
    );
}

function getSingleColumnCustomErrorMessage(
    question: QuestionModel,
    customErrorKey: string,
    language: string,
) {
    const isSingleColumnStarRatingQuestion =
        question.type === QuestionType.StarRatingGrid && question.columns?.length === 1;

    if (isSingleColumnStarRatingQuestion) {
        const [column] = question.columns;
        return translate(column, customErrorKey, language);
    }

    return "";
}

function getMessageText(props: Props) {
    const { error, question, questionnaire, language } = props;
    if (!error) return "";

    // For text and open end questions, when error is IncompleteAnswer and answer is empty,
    // show the default error message instead of custom error message
    const isTextOrOpenEnd =
        question.type === QuestionType.TextAnswer || question.type === QuestionType.OpenEndAnswer;
    const isIncompleteAnswerError = error.key === "IncompleteAnswer";

    // If this is a text/open end question with IncompleteAnswer error
    if (isTextOrOpenEnd && isIncompleteAnswerError) {
        const key = error.key || "InvalidAnswer";
        const text =
            error.message ||
            translate(question, key, language) ||
            translate(questionnaire, key, language) ||
            key;
        return replaceArgs(text, error.args);
    }

    let text = getCustomErrorMessage(props);
    if (!text) {
        const key = error.key || "InvalidAnswer";
        text =
            error.message ||
            translate(question, key, language) ||
            translate(questionnaire, key, language) ||
            key;
    }
    return replaceArgs(text, error.args);
}

export default function ValidationError(props: Props) {
    const theme = useTheme() as Theme;
    const classes = useStyles({ theme });
    const dimension = useDimensionContext();
    const messageText = useRecall(getMessageText(props));

    const { question, error } = props;
    const isDisplayWide = dimension && dimension.minWidth(778);
    // Re-key the per-question alert on each failed submit so a repeated identical error
    // re-announces in NVDA (DEV-97358).
    const announceSeq = useSelector((state: StoreState) => state.interview.validationAnnounceSeq);

    if (error === undefined) {
        return null;
    }

    const iconEl = theme.UseModernTheme ? (
        <QuestionValidationErrorIcon
            glyphColor={theme.WhiteColor ?? "#FFFFFF"}
            className={classnames(classes.icon, classes.iconSvg, isDisplayWide && classes.dynamicIcon)}
        />
    ) : (
        <FontAwesomeIcon
            icon="exclamation-circle"
            className={classnames(classes.icon, isDisplayWide && classes.dynamicIcon)}
        />
    );
    const messageEl = (
        <div
            key={announceSeq}
            id={`${question.elementId}_ERROR`}
            role="alert"
            className={classnames(
                classes.validationError,
                isDisplayWide && classes.dynamicValidationError,
            )}
            dangerouslySetInnerHTML={{ __html: messageText }}
        />
    );

    if (theme.UseModernTheme) {
        return (
            <div className={classes.errorRow}>
                {iconEl}
                {messageEl}
            </div>
        );
    }

    return (
        <>
            {iconEl}
            {messageEl}
        </>
    );
}
